/**
 * 리포트 — 기간과 프로젝트를 골라 만드는 집계 한 건. 주간 보고는 정해진 요일에 같은 조건으로
 * 이 행을 만드는 트리거일 뿐 별도 산출물이 아니다(backend ADR
 * `2026-10-27-absorb-weekly-into-reports.md`).
 *
 * 집계는 만드는 시점에 `payload` 로 굳는다 — 뒤에 Daily 가 고쳐져도 이미 나간 숫자는 바뀌지
 * 않는다. 어느 시점 집계인지는 `generated_at` 으로 화면에 그대로 표기한다. 중복은 막지 않는다:
 * 같은 기간을 열 번 뽑으면 열 건이 남는다.
 *
 * 주간 개요와 진행 예정은 별도 칸이 아니라 코멘트로 오간다. 리포트가 자동 생성물이라 만들어지는
 * 시점에 사람 글이 아직 없기 때문이다.
 */

export type ReportStatus = "pending" | "ready" | "failed";

/** payload 의 모양 버전. 지표를 더하면 지난 리포트에는 그 칸이 없으므로 없는 칸을 견딘다. */
export const PAYLOAD_VERSION = 1;

export interface ReportHours {
  total: number;
  regular: number;
  overtime: number;
  /** 바로 앞 같은 길이의 기간 — 전기간 대비 */
  previous_total: number;
  previous_overtime: number;
}

/** 프로젝트 섹션의 한 사람 — 그 기간에 이 프로젝트에 쓴 시간과 적은 한 일. */
export interface ReportMember {
  user_id: string;
  user_name: string;
  hours: number;
  plan_hours: number;
  /**
   * Daily 의 「한 일」을 중복 없이 모은 것 — 근거 Daily 라 관리자에게만 온다. 관리자가 아니면 키가 없다
   * → backend `decisions/2026-10-30-show-daily-text-to-the-admin-only.md`.
   */
  descriptions?: string[];
}

/** 프로젝트 하나를 보고하는 한 단락. 순서는 보고할 때 넘기는 순서다. */
export interface ReportProjectSection {
  project_id: string;
  code: string | null;
  name: string;
  color: string;
  status: string | null;
  hours: number;
  plan_hours: number;
  /** 계획 달성률(0~1). 계획이 없으면 null */
  plan_achievement: number | null;
  /** 그 기간에 완료 처리한 마일스톤 수 */
  milestones_done: number;
  members: ReportMember[];
}

export interface ReportNonProjectSection {
  category_id: string;
  name: string;
  hours: number;
}

export interface ReportPayload {
  version: number;
  period: { from: string; to: string };
  hours: ReportHours;
  projects: ReportProjectSection[];
  non_project: ReportNonProjectSection[];
}

export interface ReportComment {
  id: string;
  /** 프로젝트 섹션에 달린 것. 리포트 전체에 달렸으면 null */
  project_id: string | null;
  /** 답글이면 원 코멘트. 답글의 답글은 없다 */
  parent_id: string | null;
  author_id: string;
  body: string;
  created_at: string;
  updated_at: string | null;
}

/*
 * ── AI 가 쓴 글 (잠정) ─────────────────────────────────────────────────────
 *
 * 리포트는 AI 가 집계와 Daily 를 읽고 쓰는 보고서다. 아래 모양은 화면을 먼저 그리려고 잡은
 * **잠정 규약**이다 — AI API 규약을 다시 정하면 바뀐다. 그래서 모든 칸을 비어 있을 수 있게 두고,
 * 화면은 글이 없으면 숫자만으로 그린다. 글도 집계처럼 만드는 시점에 굳는다.
 */

/** 글이 무엇을 보고 썼는지 — 근거가 된 Daily 작업 한 줄. 보드의 작업 카드와 같은 칸을 쓴다. */
export interface ReportEvidence {
  daily_row_id: string;
  work_date: string;
  user_name: string;
  /** 계획 문구(없으면 한 일) */
  title: string;
  category_name: string | null;
  plan_hours: number;
  actual_hours: number;
  description: string;
  completed_at: string | null;
}

/** 한 사람이 그 프로젝트에서 한 일의 요약. */
export interface ReportStoryMember {
  user_id: string;
  summary: string;
}

/** 프로젝트 하나에 대해 쓴 단락. */
export interface ReportStorySection {
  project_id: string;
  /** 목록 카드의 「다루는 것」 한 줄 */
  topic: string;
  body: string;
  /** 다음 기간에 할 일 */
  next: string;
  /** 사람마다 이 프로젝트에서 한 일을 한 줄로 — Weekly 업무표의 업무 내용 칸 */
  members?: ReportStoryMember[];
  /** 근거가 된 Daily 카드 — 관리자에게만 채워진다. 아니면 빈 목록이다 */
  evidence: ReportEvidence[];
}

export interface ReportStory {
  /** 보고서 제목 — 그 기간을 한 문장으로 */
  headline: string;
  /** 목록 카드에 싣는 두세 문장 */
  summary: string;
  /** 「이번 주 요점」 */
  points: string[];
  sections: ReportStorySection[];
  /** 「살펴볼 것」 — 상한을 넘긴 사람, 계획 없이 늘어난 일 같은 것 */
  watch: string[];
}

/** 목록 카드의 「다루는 것」 — 프로젝트 칩과 한 줄. */
export interface ReportTopic {
  project_id: string;
  code: string | null;
  name: string;
  color: string;
  line: string;
}

/** 목록의 한 줄 — 집계는 들어 있지 않다. */
export interface ReportRow {
  id: string;
  /** 자동 생성이면 null */
  requested_by: string | null;
  /** 트리거가 만들었으면 그 트리거 */
  schedule_id: string | null;
  status: ReportStatus;
  period_from: string;
  period_to: string;
  /** 집계 기준 시각. 만들다 실패했으면 null */
  generated_at: string | null;
  failure_reason: string;
  created_at: string;
  /** 만들 때 덧붙인 요청 — 직접 만든 리포트에만 */
  custom_prompt?: string | null;
  /* 아래는 AI 글에서 뽑은 목록용 요약(잠정). 글이 없으면 비어 있다. */
  headline?: string | null;
  summary?: string | null;
  topics?: ReportTopic[];
  comment_count?: number;
}

export interface Report extends ReportRow {
  /** 만들다 실패했으면 null */
  payload: ReportPayload | null;
  /** 이 사람이 금액을 볼 수 있는가 — 금액은 payload 가 아니라 조회 때 붙는다 */
  money_visible: boolean;
  comments: ReportComment[];
  /** AI 가 쓴 글(잠정). 아직 없거나 쓰지 못했으면 null */
  story?: ReportStory | null;
}

/** 리포트 만들기 — 프로젝트를 비우면 그 시점의 진행 중 프로젝트를 담는 전사 리포트가 된다. */
export interface ReportCreateInput {
  period_from: string;
  period_to: string;
  project_ids: string[];
}

export interface ReportCommentInput {
  /** 프로젝트 섹션에 달 때만. 비우면 리포트 전체 */
  project_id?: string | null;
  /** 답글이면 원 코멘트 */
  parent_id?: string | null;
  body: string;
}
